import { useRef } from 'react';

interface EventItem {
  name: string;
  location: string;
  locationType: 'Online' | 'In Person';
  details: string;
  date: string;
}

const events: EventItem[] = [];

export default function Events() {
  const scrollRef = useRef<HTMLDivElement>(null);

  if (events.length === 0) return null;

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const cardWidth = scrollRef.current.querySelector('.event-card')?.clientWidth || 300;
    const gap = 24;
    const distance = cardWidth + gap;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -distance : distance,
      behavior: 'smooth',
    });
  };

  return (
    <section id="events" className="py-16 md:py-24 bg-matou-bg">
      <div className="max-w-content mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="bg-matou-teal-deep rounded-t-md px-5 py-3 flex items-center justify-between">
          <h2 className="font-heading text-white text-sm font-bold uppercase tracking-widest">
            Upcoming Events
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() => scroll('left')}
              className="text-white/70 hover:text-white transition-colors p-1"
              aria-label="Scroll left"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => scroll('right')}
              className="text-white/70 hover:text-white transition-colors p-1"
              aria-label="Scroll right"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        <div className="border border-t-0 border-black/10 rounded-b-md bg-matou-surface p-6 md:p-8">
          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {events.map((event, i) => (
              <div
                key={i}
                className="event-card snap-start flex-shrink-0 w-[280px] sm:w-[300px] bg-matou-bg border border-black/10 rounded-lg overflow-hidden hover:-translate-y-0.5 transition-transform duration-150"
              >
                {/* Thumbnail placeholder */}
                <div className="h-36 bg-gradient-to-br from-matou-teal-dark to-matou-teal-deep flex items-center justify-center">
                  <pre className="text-matou-teal-soft text-xs font-mono select-none text-center" aria-hidden="true">{`
  ╭────────╮
  │ EVENT  │
  │  ${event.date.padStart(4)}  │
  ╰────────╯`}</pre>
                </div>

                <div className="p-4">
                  {/* Location badge */}
                  <span
                    className={`inline-block font-mono text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded mb-3 ${
                      event.locationType === 'Online'
                        ? 'bg-economy-fill text-economy-stroke'
                        : 'bg-growth-fill text-growth-stroke'
                    }`}
                  >
                    {event.locationType}
                  </span>

                  <h3 className="font-heading text-sm font-bold text-matou-text mb-2 leading-snug">
                    {event.name}
                  </h3>

                  <p className="font-mono text-xs text-matou-text-muted mb-1">
                    {event.location}
                  </p>

                  <p className="font-mono text-xs text-matou-text-muted leading-relaxed mb-4 line-clamp-3">
                    {event.details}
                  </p>

                  <button className="w-full font-mono text-xs font-semibold bg-matou-teal-medium text-white px-4 py-2 rounded-md hover:bg-matou-teal-deep transition-colors duration-150">
                    RSVP
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
}
